import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { apiPost } from '../utils/api';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaPaperPlane, FaCheckCircle } from 'react-icons/fa';
import '../styles/Contact.css';

function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [rateLimitError, setRateLimitError] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const onSubmit = async (formData) => {
    try {
      setSubmitting(true);
      setRateLimitError(false);
      
      // Add timestamp to prevent caching issues
      const response = await apiPost({
        action: 'contact',
        ...formData,
        timestamp: new Date().toISOString()
      });

      // Check if response indicates success
      if (response && response.success) {
        setSubmitSuccess(true);
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        reset();
        
        // Reset success message after 5 seconds
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        // Handle specific error cases
        if (response?.error === 'email_limit_exceeded') {
          setRateLimitError(true);
          toast.error('You\'ve reached the message limit. Please try again later or contact us directly.');
        } else if (response?.error === 'duplicate_message') {
          toast.error('You\'ve already sent a similar message. Please wait for our response.');
        } else {
          toast.error(response?.message || 'Failed to send message');
        }
      }
    } catch (error) {
      console.error('Contact form error:', error);
      
      // Handle different error types
      if (error.response?.status === 429) {
        setRateLimitError(true);
        toast.error('Too many messages. Please wait before trying again.');
      } else if (error.response?.data?.error === 'email_already_used') {
        toast.error('This email has already been used. Please use a different email or contact us directly.');
      } else if (error.response?.data?.error === 'rate_limit_exceeded') {
        setRateLimitError(true);
        toast.error('Message limit reached. Please try again in 24 hours.');
      } else {
        toast.error('Network error. Please check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle manual retry
  const handleRetry = () => {
    setRateLimitError(false);
    setSubmitSuccess(false);
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="contact-hero-title">Let's Create Magic Together</h1>
          <p className="contact-hero-subtitle">
            Ready to capture your special moments? Get in touch with us to discuss 
            your vision and how we can help bring it to life.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="contact-info-section">
        <div className="container">
          <div className="info-cards">
            <div className="info-card">
              <div className="info-icon">
                <FaPhone />
              </div>
              <h3 className="info-title">Call Us</h3>
              <p className="info-content">
                <a href="tel:+437-973-4414">437-973-4414</a>
              </p>
              <p className="info-note">Mon-Fri, 9am-6pm</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FaEnvelope />
              </div>
              <h3 className="info-title">Email Us</h3>
              <p className="info-content">
                <a href="mailto:Cinehousestudio05@gmail.com">Cinehousestudio05@gmail.com</a>
              </p>
              <p className="info-note">24/7 Support</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FaMapMarkerAlt />
              </div>
              <h3 className="info-title">Visit Us</h3>
              <p className="info-content">
                Canada
              </p>
              <p className="info-note">By appointment only</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <FaClock />
              </div>
              <h3 className="info-title">Working Days</h3>
              <p className="info-content">
                Monday - Saturday<br />
                Sunday: By Appointment
              </p>
              <p className="info-note">Weekend appointments available</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-grid">
            {/* Left Column - Image */}
            <div className="contact-image-col">
              <div className="contact-image-wrapper">
                <img 
                  src="https://images.unsplash.com/photo-1519741497674-611481863552?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" 
                  alt="Wedding photography"
                  className="contact-image"
                />
                <div className="image-overlay">
                  <h3 className="overlay-title">Your Story Matters</h3>
                  <p className="overlay-text">
                    Every love story is unique. Let us help you tell yours through timeless imagery.
                  </p>
                </div>
              </div>
              
              <div className="contact-testimonial">
                <div className="testimonial-quote">"</div>
                <p className="testimonial-text">
                  CineHouseStudio captured our wedding beautifully. Their attention to detail and 
                  artistic vision exceeded our expectations.
                </p>
                <div className="testimonial-author">
                  <strong>- Sarah & Michael</strong>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="contact-form-col">
              <div className="form-header">
                <h2 className="form-title">Send us a Message</h2>
                <p className="form-subtitle">
                  Fill out the form below and we'll get back to you within 24 hours
                </p>
              </div>

              {/* Success Message */}
              {submitSuccess && (
                <div className="success-message">
                  <FaCheckCircle className="success-icon" />
                  <div className="success-content">
                    <h4>Message Sent Successfully!</h4>
                    <p>We'll get back to you within 24 hours. Check your email for a confirmation.</p>
                  </div>
                </div>
              )}

              {/* Rate Limit Error Message */}
              {rateLimitError && (
                <div className="rate-limit-error">
                  <h4>Message Limit Reached</h4>
                  <p>You've reached the maximum number of messages. Please try one of these alternatives:</p>
                  <ul>
                    <li>Call us directly: <strong>+1 (234) 567-890</strong></li>
                    <li>Email us: <strong>info@cinehousestudio.com</strong></li>
                    <li>Try again in 24 hours</li>
                  </ul>
                  <button onClick={handleRetry} className="retry-button">
                    Try Again Later
                  </button>
                </div>
              )}

              {/* Contact Form */}
              {!rateLimitError && (
                <form onSubmit={handleSubmit(onSubmit)} className="contact-form">
                  {/* Name Field */}
                  <div className="form-group">
                    <label className="form-label">
                      Your Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter your full name"
                      className={`form-input ${errors.name ? 'error' : ''}`}
                      {...register('name', { 
                        required: 'Name is required',
                        minLength: {
                          value: 2,
                          message: 'Name must be at least 2 characters'
                        },
                        maxLength: {
                          value: 100,
                          message: 'Name cannot exceed 100 characters'
                        }
                      })}
                    />
                    {errors.name && (
                      <span className="error-message">{errors.name.message}</span>
                    )}
                  </div>

                  {/* Email Field */}
                  <div className="form-group">
                    <label className="form-label">
                      Email Address <span className="required">*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      className={`form-input ${errors.email ? 'error' : ''}`}
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Please enter a valid email address'
                        }
                      })}
                    />
                    {errors.email && (
                      <span className="error-message">{errors.email.message}</span>
                    )}
                    <small className="field-note">
                      We'll never share your email with anyone else.
                    </small>
                  </div>

                  {/* Phone Field */}
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input
                      type="tel"
                      placeholder="Enter your phone number"
                      className="form-input"
                      {...register('phone', {
                        pattern: {
                          value: /^[0-9+\-\s()]*$/,
                          message: 'Please enter a valid phone number'
                        }
                      })}
                    />
                    {errors.phone && (
                      <span className="error-message">{errors.phone.message}</span>
                    )}
                  </div>

                  {/* Package Selection */}
                  <div className="form-group">
                    <label className="form-label">Interested Package</label>
                    <select 
                      className="form-select"
                      {...register('package')}
                    >
                      <option value="">Select a package (Optional)</option>
                      <option value="photography">Photography Only</option>
                      <option value="videography">Videography Only</option>
                      <option value="both">Both Photography & Videography</option>
                      <option value="custom">Custom Package</option>
                      <option value="consultation">Free Consultation</option>
                    </select>
                  </div>

                  {/* Event Date */}
                  <div className="form-group">
                    <label className="form-label">Event Date</label>
                    <input
                      type="date"
                      className="form-input"
                      {...register('bookingDate')}
                    />
                  </div>

                  {/* Message Field */}
                  <div className="form-group">
                    <label className="form-label">
                      Your Message <span className="required">*</span>
                    </label>
                    <textarea
                      rows="6"
                      placeholder="Tell us about your wedding, vision, and any specific requirements..."
                      className={`form-textarea ${errors.message ? 'error' : ''}`}
                      {...register('message', { 
                        required: 'Message is required',
                        minLength: {
                          value: 20,
                          message: 'Please provide more details (at least 20 characters)'
                        },
                        maxLength: {
                          value: 2000,
                          message: 'Message cannot exceed 2000 characters'
                        }
                      })}
                    />
                    {errors.message && (
                      <span className="error-message">{errors.message.message}</span>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="form-actions">
                    <button 
                      type="submit" 
                      className="submit-button"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-small"></span>
                          Sending...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane className="button-icon" />
                          Send Message
                        </>
                      )}
                    </button>
                  </div>

                  {/* Privacy Notice */}
                  <div className="privacy-notice">
                    <p>
                      By submitting this form, you agree to our 
                      <a href="/privacy" target="_blank" rel="noopener noreferrer"> Privacy Policy</a> and 
                      <a href="/terms" target="_blank" rel="noopener noreferrer"> Terms of Service</a>.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>


      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="faq-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3 className="faq-question">How far in advance should I book?</h3>
              <p className="faq-answer">
                We recommend booking at least 6-12 months in advance, especially for peak wedding season (May-October).
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Do you travel for destination weddings?</h3>
              <p className="faq-answer">
                Yes! We love capturing love stories anywhere in the world. Contact us for destination wedding packages.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">What's your pricing structure?</h3>
              <p className="faq-answer">
                We offer customizable packages starting from $3,500. Contact us for a detailed quote based on your needs.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">How long until we receive our photos?</h3>
              <p className="faq-answer">
                You'll receive a sneak peek within 48 hours, and the full gallery within 6-8 weeks.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="emergency-contact">
        <div className="container">
          <div className="emergency-banner">
            <h3>Urgent Inquiry?</h3>
            <p>For immediate assistance, please call us directly at <strong>437-973-4414</strong></p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Contact;